// src/components/dashboard/AdminSettings.js
import React, { useState, useEffect } from "react";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db, auth } from "../../firebase/config";
import { 
  User, Lock, Bell, Palette, Save, Eye, EyeOff, 
  Camera, Mail, Phone, CheckCircle, AlertCircle 
} from "lucide-react";

// Import du système de notifications
import NotificationDispatcher from "../../services/NotificationDispatcher";
import useNotificationSystem from "../../hooks/useNotificationSystem";

const AdminSettings = () => {
  const { user } = useAuth();
  
  console.log("🎯 AdminSettings loaded");
  
  // Hook système de notifications
  const { 
    sendNotification, 
    notifyDossierUpdate,
    isLoading: notifLoading,
    error: notifError 
  } = useNotificationSystem();
  
  // Solution de fallback si ThemeContext n'existe pas
  const themeContext = useTheme();
  const [localTheme, setLocalTheme] = useState("light");

  const theme = themeContext?.theme || localTheme;
  const setTheme = themeContext?.setTheme || ((newTheme) => {
    console.log("🎨 Changement de thème local:", newTheme);
    setLocalTheme(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
      document.body.style.backgroundColor = '#1f2937';
      document.body.style.color = '#f9fafb';
    } else {
      document.documentElement.classList.remove('dark');
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
    }
  });

  console.log("🎨 Thème actuel:", theme);
  console.log("🎨 ThemeContext:", themeContext);

  // États pour les sections
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  
  // États pour le profil
  const [profileData, setProfileData] = useState({
    displayName: "",
    email: "",
    phone: "",
    role: ""
  });

  // États pour le changement de mot de passe
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // États pour les notifications
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    reminderDays: 3
  });

  // Charger les données utilisateur
  useEffect(() => {
    const loadUserData = async () => {
      if (!user?.uid) return;
      
      try {
        setLoading(true);
        const userDoc = await getDoc(doc(db, "users", user.uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setProfileData({
            displayName: userData.displayName || user.displayName || "",
            email: user.email || "",
            phone: userData.phone || "",
            role: userData.role || ""
          });
          
          setNotificationSettings({
            emailNotifications: userData.emailNotifications !== false,
            smsNotifications: userData.smsNotifications || false,
            pushNotifications: userData.pushNotifications !== false,
            reminderDays: userData.reminderDays || 3
          });
        }
      } catch (err) {
        setError("Erreur lors du chargement des données");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user]);

  // Sauvegarder le profil
  const handleSaveProfile = async () => {
    if (!user?.uid) {
      setError("Utilisateur non connecté");
      return;
    }
    
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      console.log("🔄 Sauvegarde du profil admin");

      await updateDoc(doc(db, "users", user.uid), {
        displayName: profileData.displayName,
        phone: profileData.phone,
        updatedAt: new Date()
      });

      console.log("✅ Profil admin sauvegardé!");
      setSuccess("Profil mis à jour avec succès !");
      setTimeout(() => setSuccess(""), 3000);
      
    } catch (err) {
      console.error("❌ Erreur:", err);
      setError("Erreur lors de la mise à jour du profil: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Changer le mot de passe
  const handleChangePassword = async () => {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      setError("Utilisateur non connecté");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError("Les nouveaux mots de passe ne correspondent pas");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError("Le nouveau mot de passe doit contenir au moins 6 caractères");
      return;
    }

    if (!passwordData.currentPassword) {
      setError("Veuillez entrer votre mot de passe actuel");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const credential = EmailAuthProvider.credential(currentUser.email, passwordData.currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, passwordData.newPassword);
      
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });

      setSuccess("Mot de passe modifié avec succès !");
      setTimeout(() => setSuccess(""), 3000);

    } catch (err) {
      console.error("❌ Erreur changement mot de passe:", err);
      
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError("Le mot de passe actuel est incorrect");
      } else if (err.code === 'auth/weak-password') {
        setError("Le nouveau mot de passe est trop faible");
      } else if (err.code === 'auth/requires-recent-login') {
        setError("Veuillez vous déconnecter et vous reconnecter avant de changer votre mot de passe");
      } else if (err.code === 'auth/too-many-requests') {
        setError("Trop de tentatives. Veuillez réessayer dans quelques minutes");
      } else {
        setError("Erreur lors du changement de mot de passe: " + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Sauvegarder les préférences de notifications
  const handleSaveNotifications = async () => {
    if (!user?.uid) {
      setError("Utilisateur non connecté");
      return;
    }
    
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      console.log("💾 Sauvegarde notifications admin:", notificationSettings);

      await updateDoc(doc(db, "users", user.uid), {
        emailNotifications: notificationSettings.emailNotifications,
        smsNotifications: notificationSettings.smsNotifications,
        pushNotifications: notificationSettings.pushNotifications,
        reminderDays: notificationSettings.reminderDays,
        updatedAt: new Date()
      });

      console.log("✅ Notifications admin sauvegardées!");
      setSuccess("Préférences de notifications sauvegardées avec succès !");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("❌ Erreur sauvegarde notifications:", err);
      setError("Erreur lors de la sauvegarde des notifications: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Test système de notifications
  const testSystemeComplet = async () => {
    try {
      console.log('🚀 Test système complet depuis AdminSettings...');
      
      console.log('📤 Envoi notification normale...');
      await sendNotification({
        recipientId: user.uid,
        type: 'admin_test',
        title: 'Test depuis paramètres admin',
        message: `Configuration testée - Email:${notificationSettings.emailNotifications ? 'ON' : 'OFF'}, SMS:${notificationSettings.smsNotifications ? 'ON' : 'OFF'}, Push:${notificationSettings.pushNotifications ? 'ON' : 'OFF'}`,
        urgency: 'normal'
      });
      
      console.log('✅ Test 1 terminé - notification normale');
      
      setTimeout(async () => {
        console.log('📤 Envoi notification urgente...');
        await sendNotification({
          recipientId: user.uid,
          type: 'admin_test',
          title: '🚨 Test notification urgente',
          message: 'Test des notifications urgentes (SMS + Email + Push)',
          urgency: 'urgent'
        });
        
        console.log('✅ Test 2 terminé - notification urgente');
      }, 2000);
      
      const confirmToast = document.createElement('div');
      confirmToast.innerHTML = `
        <div style="background: linear-gradient(135deg, #10b981, #059669); color: white; padding: 20px; border-radius: 12px; box-shadow: 0 8px 32px rgba(0,0,0,0.3);">
          <div style="display: flex; align-items: center;">
            <span style="font-size: 32px; margin-right: 15px;">🎉</span>
            <div>
              <h3 style="margin: 0 0 8px 0;">Tests lancés avec succès !</h3>
              <p style="margin: 0; font-size: 14px; opacity: 0.9;">
                Vérifiez vos emails, SMS et notifications push selon vos paramètres.
              </p>
              <small style="opacity: 0.7;">2 notifications envoyées (normale + urgente)</small>
            </div>
            <button onclick="this.parentElement.parentElement.parentElement.remove()" style="background: rgba(255,255,255,0.2); border: none; color: white; border-radius: 50%; width: 30px; height: 30px; margin-left: 15px; cursor: pointer; font-size: 16px;">×</button>
          </div>
        </div>
      `;
      confirmToast.style.cssText = 'position: fixed; top: 20px; right: 20px; z-index: 999999;';
      document.body.appendChild(confirmToast);
      setTimeout(() => {
        if (confirmToast.parentNode) {
          confirmToast.remove();
        }
      }, 8000);
      
    } catch (error) {
      console.error('❌ Erreur test système:', error);
      
      const errorToast = document.createElement('div');
      errorToast.innerHTML = `
        <div style="background: linear-gradient(135deg, #dc2626, #b91c1c); color: white; padding: 20px; border-radius: 12px; box-shadow: 0 8px 32px rgba(0,0,0,0.3);">
          <div style="display: flex; align-items: center;">
            <span style="font-size: 32px; margin-right: 15px;">❌</span>
            <div>
              <h3 style="margin: 0 0 8px 0;">Erreur lors du test</h3>
              <p style="margin: 0; font-size: 14px; opacity: 0.9;">
                ${error.message}
              </p>
            </div>
          </div>
        </div>
      `;
      errorToast.style.cssText = 'position: fixed; top: 20px; right: 20px; z-index: 999999;';
      document.body.appendChild(errorToast);
      setTimeout(() => errorToast.remove(), 5000);
    }
  };

  // Test simple avec toast
  const testNotificationSimple = () => {
    console.log('🧪 Test notification simple...');
    
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border-radius: 12px;
      padding: 20px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.3);
      z-index: 999999;
      max-width: 350px;
      font-family: Arial, sans-serif;
      border: 2px solid rgba(255,255,255,0.2);
    `;
    
    toast.innerHTML = `
      <div style="display: flex; align-items: center;">
        <span style="font-size: 28px; margin-right: 15px;">🎉</span>
        <div>
          <h4 style="margin: 0 0 8px 0; font-size: 16px;">Système de notifications actif !</h4>
          <p style="margin: 0; font-size: 14px; opacity: 0.9;">
            Email: ${notificationSettings.emailNotifications ? '✅' : '❌'} | 
            SMS: ${notificationSettings.smsNotifications ? '✅' : '❌'} | 
            Push: ${notificationSettings.pushNotifications ? '✅' : '❌'}
          </p>
          <small style="opacity: 0.7;">Délai de rappel: ${notificationSettings.reminderDays} jours</small>
        </div>
        <button onclick="this.parentElement.parentElement.remove()" 
                style="background: rgba(255,255,255,0.2); border: none; color: white; border-radius: 50%; width: 30px; height: 30px; margin-left: 15px; cursor: pointer; font-size: 16px;">×</button>
      </div>
    `;
    
    document.body.appendChild(toast);
    
    setTimeout(() => {
      if (toast.parentNode) {
        toast.style.transform = 'translateX(400px)';
        toast.style.transition = 'transform 0.3s ease-out';
        setTimeout(() => toast.remove(), 300);
      }
    }, 5000);
    
    console.log('✅ Test de notification système terminé !');
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <div className={`min-h-screen p-6 transition-colors duration-300 ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white'
        : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'
    }`}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-4xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Paramètres
          </h1>
          <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
            Gérez votre profil et vos préférences
          </p>
        </div>

        {/* Messages de feedback */}
        {success && (
          <div className={`mb-6 p-4 border-l-4 border-green-400 rounded-lg ${
            theme === 'dark' ? 'bg-green-900 bg-opacity-50' : 'bg-green-50'
          }`}>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
              <p className={`${theme === 'dark' ? 'text-green-300' : 'text-green-700'}`}>
                {success}
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className={`mb-6 p-4 border-l-4 border-red-400 rounded-lg ${
            theme === 'dark' ? 'bg-red-900 bg-opacity-50' : 'bg-red-50'
          }`}>
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-400 mr-3" />
              <p className={`${theme === 'dark' ? 'text-red-300' : 'text-red-700'}`}>
                {error}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Section Profil */}
          <div className={`rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 ${
            theme === 'dark' 
              ? 'bg-gray-800 text-white' 
              : 'bg-white'
          }`}>
            <div className="flex items-center mb-6">
              <div className={`p-3 rounded-full mr-4 ${
                theme === 'dark' ? 'bg-blue-900' : 'bg-blue-100'
              }`}>
                <User className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Profil Utilisateur
              </h2>
            </div>

            <div className="space-y-6">
              {/* Photo de profil */}
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {profileData.displayName?.charAt(0)?.toUpperCase() || "U"}
                </div>
                <button className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  theme === 'dark' 
                    ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}>
                  <Camera className="w-4 h-4 mr-2" />
                  Changer la photo
                </button>
              </div>

              {/* Nom */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Nom complet
                </label>
                <input
                  type="text"
                  value={profileData.displayName}
                  onChange={(e) => setProfileData(prev => ({
                    ...prev,
                    displayName: e.target.value
                  }))}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'border-gray-300 bg-white'
                  }`}
                  placeholder="Votre nom complet"
                />
              </div>

              {/* Email */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Adresse email
                </label>
                <div className="relative">
                  <Mail className={`absolute left-3 top-3 w-5 h-5 ${
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                  }`} />
                  <input
                    type="email"
                    value={profileData.email}
                    disabled
                    className={`w-full pl-12 pr-4 py-3 border rounded-lg ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-gray-400' 
                        : 'border-gray-300 bg-gray-50 text-gray-500'
                    }`}
                  />
                </div>
              </div>

              {/* Téléphone */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Téléphone
                </label>
                <div className="relative">
                  <Phone className={`absolute left-3 top-3 w-5 h-5 ${
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                  }`} />
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData(prev => ({
                      ...prev,
                      phone: e.target.value
                    }))}
                    className={`w-full pl-12 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'border-gray-300 bg-white'
                    }`}
                    placeholder="+33 6 12 34 56 78"
                  />
                </div>
              </div>

              {/* Rôle */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Rôle
                </label>
                <input
                  type="text"
                  value={profileData.role}
                  disabled
                  className={`w-full px-4 py-3 border rounded-lg ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-gray-400' 
                      : 'border-gray-300 bg-gray-50 text-gray-500'
                  }`}
                />
              </div>

              <button
                onClick={handleSaveProfile}
                disabled={loading}
                className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50"
              >
                <Save className="w-5 h-5 mr-2" />
                {loading ? "Sauvegarde..." : "Sauvegarder le profil"}
              </button>
            </div>
          </div>

          {/* Section Sécurité */}
          <div className={`rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 ${
            theme === 'dark' 
              ? 'bg-gray-800 text-white' 
              : 'bg-white'
          }`}>
            <div className="flex items-center mb-6">
              <div className={`p-3 rounded-full mr-4 ${
                theme === 'dark' ? 'bg-red-900' : 'bg-red-100'
              }`}>
                <Lock className="w-6 h-6 text-red-600" />
              </div>
              <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Sécurité
              </h2>
            </div>

            <div className="space-y-6">
              {/* Mot de passe actuel */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Mot de passe actuel
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData(prev => ({
                      ...prev,
                      currentPassword: e.target.value
                    }))}
                    className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'border-gray-300 bg-white'
                    }`}
                    placeholder="Entrez votre mot de passe actuel"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    className={`absolute right-3 top-3 ${
                      theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Nouveau mot de passe */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Nouveau mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({
                      ...prev,
                      newPassword: e.target.value
                    }))}
                    className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'border-gray-300 bg-white'
                    }`}
                    placeholder="Entrez votre nouveau mot de passe"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className={`absolute right-3 top-3 ${
                      theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Confirmer le mot de passe */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Confirmer le nouveau mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({
                      ...prev,
                      confirmPassword: e.target.value
                    }))}
                    className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'border-gray-300 bg-white'
                    }`}
                    placeholder="Confirmez votre nouveau mot de passe"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className={`absolute right-3 top-3 ${
                      theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                onClick={handleChangePassword}
                disabled={loading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-lg hover:from-red-600 hover:to-pink-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all disabled:opacity-50"
              >
                <Lock className="w-5 h-5 mr-2" />
                {loading ? "Changement..." : "Changer le mot de passe"}
              </button>
            </div>
          </div>

          {/* Section Apparence */}
          <div className={`rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 ${
            theme === 'dark' 
              ? 'bg-gray-800 text-white' 
              : 'bg-white'
          }`}>
            <div className="flex items-center mb-6">
              <div className={`p-3 rounded-full mr-4 ${
                theme === 'dark' ? 'bg-purple-900' : 'bg-purple-100'
              }`}>
                <Palette className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Apparence
              </h2>
            </div>

            <div className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-4 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Thème de l'interface
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setTheme("light")}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      theme === "light"
                        ? 'border-purple-500 bg-purple-50'
                        : theme === 'dark'
                        ? 'border-gray-600 hover:border-purple-400 bg-gray-700'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="w-full h-8 bg-white rounded mb-2 border"></div>
                    <p className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      Clair
                    </p>
                  </button>
                  <button
                    onClick={() => setTheme("dark")}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      theme === "dark"
                        ? 'border-purple-500 bg-purple-900 bg-opacity-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="w-full h-8 bg-gray-800 rounded mb-2"></div>
                    <p className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      Sombre
                    </p>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Section Notifications */}
          <div className={`rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 ${
            theme === 'dark' 
              ? 'bg-gray-800 text-white' 
              : 'bg-white'
          }`}>
            <div className="flex items-center mb-6">
              <div className={`p-3 rounded-full mr-4 ${
                theme === 'dark' ? 'bg-green-900' : 'bg-green-100'
              }`}>
                <Bell className="w-6 h-6 text-green-600" />
              </div>
              <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Notifications
              </h2>
            </div>

            <div className="space-y-6">
              {/* Email */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Notifications par email
                  </h3>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Recevez des emails pour les mises à jour importantes
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationSettings.emailNotifications}
                    onChange={(e) => setNotificationSettings(prev => ({
                      ...prev,
                      emailNotifications: e.target.checked
                    }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>

              {/* SMS */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Notifications par SMS
                  </h3>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Recevez des SMS pour les alertes urgentes
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationSettings.smsNotifications}
                    onChange={(e) => setNotificationSettings(prev => ({
                      ...prev,
                      smsNotifications: e.target.checked
                    }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>

              {/* Push */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Notifications push
                  </h3>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Recevez des notifications sur votre navigateur
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationSettings.pushNotifications}
                    onChange={(e) => setNotificationSettings(prev => ({
                      ...prev,
                      pushNotifications: e.target.checked
                    }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>

              {/* Délai de rappel */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Délai de rappel (jours)
                </label>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={notificationSettings.reminderDays}
                  onChange={(e) => setNotificationSettings(prev => ({
                    ...prev,
                    reminderDays: parseInt(e.target.value) || 3
                  }))}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                    theme === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'border-gray-300 bg-white'
                  }`}
                />
                <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  Nombre de jours avant l'échéance pour recevoir un rappel automatique
                </p>
              </div>

              {/* Section test */}
              <div className="border-t pt-6 space-y-3">
                <h4 className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Tests du système
                </h4>
                
                <div className="grid grid-cols-1 gap-3">
                  {/* Bouton sauvegarde */}
                  <button
                    onClick={handleSaveNotifications}
                    disabled={loading}
                    className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all disabled:opacity-50"
                  >
                    <Bell className="w-5 h-5 mr-2" />
                    {loading ? "Sauvegarde..." : "Sauvegarder les notifications"}
                  </button>
                  
                  {/* Bouton test simple */}
                  <button 
                    onClick={testNotificationSimple}
                    className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all transform hover:scale-105"
                  >
                    🧪 Test Interface
                  </button>
                  
                  {/* Bouton test complet */}
                  <button 
                    onClick={testSystemeComplet}
                    disabled={notifLoading}
                    className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all transform hover:scale-105 disabled:opacity-50"
                  >
                    {notifLoading ? '🔄 Test en cours...' : '🚀 Test Système Complet'}
                  </button>
                </div>
                
                {/* Infos sur les tests */}
                <div className={`text-xs p-3 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-600'
                }`}>
                  <p className="mb-1"><strong>Test Interface:</strong> Affiche vos paramètres actuels</p>
                  <p><strong>Test Complet:</strong> Envoie de vraies notifications selon vos paramètres</p>
                </div>
                
                {/* Affichage erreur notifications */}
                {notifError && (
                  <div className="text-red-500 text-sm p-2 bg-red-50 rounded">
                    Erreur: {notifError}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;